import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApprovalTask, listApprovals } from '../../api/parameter';
import { useCanAny } from '../../store';
import {
    moduleLabel,
    onApprovalChanged,
    STATUS_LABEL,
} from '../../utils/approvalLabels';
import { formatTime } from '../../utils/time';

// 顶栏审批通知 (2026-08-26): 管理员进平台就能看到有多少内容待审批, 不用主动翻审批中心.
// - 管理员: 徽标 = 全站 pending 工单数, 下拉列最近 N 条, 点条目跳审批中心并预填工单号;
// - 普通用户: 徽标 = 自己仍在审批中的工单数, 下拉列自己最近的工单及其结果 (通过/驳回).
// 数据源复用审批列表接口 (totalCount 当计数), 不新增后端接口; 轮询 + 切回标签页时刷新.

const PREVIEW_SIZE = 6;
const POLL_MS = 60_000;

const NotifyBell = () => {
    const canManage = useCanAny('approve');
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [list, setList] = useState<ApprovalTask[]>([]);
    const boxRef = useRef<HTMLDivElement>(null);

    // 管理员看全站待审批; 普通用户看自己提交的 (后端对非 admin 恒按提交人收紧)
    const centerPath = canManage ? '/admin/approvals' : '/my-approvals';

    const load = useCallback(async () => {
        try {
            // 徽标恒为"待我处理/我在等的" pending 数 (totalCount), 与下拉列表同一次请求
            const pending = await listApprovals({
                status: 'pending',
                page: 1,
                pageSize: PREVIEW_SIZE,
                scope: canManage ? undefined : 'mine',
            });
            setCount(pending.totalCount);
            if (pending.totalCount > 0 || canManage) {
                setList(pending.list || []);
                return;
            }
            // 普通用户没有待审批时, 下拉改列自己最近的工单结果 (刚被通过/驳回的能立刻看到)
            const recent = await listApprovals({
                page: 1,
                pageSize: PREVIEW_SIZE,
                scope: 'mine',
            });
            setList(recent.list || []);
        } catch {
            setCount(0);
            setList([]);
        }
    }, [canManage]);

    useEffect(() => {
        load();
        const timer = window.setInterval(load, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisible);
        // 同一标签页内审批完不会触发 visibilitychange, 靠审批中心广播的事件即时刷新
        const offChanged = onApprovalChanged(load);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            offChanged();
        };
    }, [load]);

    // 点外部关闭下拉
    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e: MouseEvent) => {
            if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const title = canManage
        ? count > 0
            ? `有 ${count} 条内容待审批`
            : '暂无待审批内容'
        : count > 0
          ? `我有 ${count} 条工单在审批中`
          : '我的工单动态';

    return (
        <div className="relative" ref={boxRef}>
            <button
                type="button"
                title={title}
                aria-label={title}
                onClick={() => {
                    setOpen((v) => !v);
                    if (!open) load();
                }}
                className="relative flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
                <span className="text-base leading-none">🔔</span>
                {count > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-rose-500 px-1 text-center text-[10px] font-semibold leading-4 text-white">
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-10 z-40 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                        <span className="text-xs font-semibold text-slate-700">
                            {title}
                        </span>
                        <Link
                            to={centerPath}
                            onClick={() => setOpen(false)}
                            className="text-xs text-brand-600 hover:underline"
                        >
                            查看全部
                        </Link>
                    </div>
                    {list.length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-slate-400">
                            暂无待处理的工单
                        </div>
                    ) : (
                        <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
                            {list.map((t) => {
                                const s = STATUS_LABEL[t.status] || {
                                    label: t.status,
                                    cls: 'bg-slate-100 text-slate-600',
                                };
                                return (
                                    <li key={t.taskCode}>
                                        <Link
                                            to={`${centerPath}?kw=${encodeURIComponent(t.taskCode)}`}
                                            onClick={() => setOpen(false)}
                                            className="block px-3 py-2 hover:bg-slate-50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`tag ${s.cls}`}
                                                >
                                                    {s.label}
                                                </span>
                                                <span className="text-[11px] text-slate-400">
                                                    {moduleLabel(t.targetType)}
                                                </span>
                                                <span className="ml-auto text-[11px] text-slate-400">
                                                    {formatTime(t.createdAt)}
                                                </span>
                                            </div>
                                            <div className="mt-1 truncate text-xs text-slate-700">
                                                {t.title}
                                            </div>
                                            <div className="truncate text-[11px] text-slate-400">
                                                提交人:{' '}
                                                {t.submittedByName ||
                                                    t.submittedBy}
                                                {t.status === 'rejected' &&
                                                t.decisionNote
                                                    ? ` · 驳回原因: ${t.decisionNote}`
                                                    : ''}
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotifyBell;
